import {
	type ClipboardEvent,
	type KeyboardEvent,
	useEffect,
	useRef,
	useState,
} from "react";
import { useNavigate } from "react-router-dom";

import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { AdminIcon } from "@/components/admin/icon";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import { useSetupTotp, useVerifyTotp } from "@/hooks/use-me";

export function MfaTotpSetupPage() {
	const navigate = useNavigate();
	const setupMutation = useSetupTotp();
	const verifyMutation = useVerifyTotp();
	const [setupId, setSetupId] = useState<string | null>(null);
	const [secret, setSecret] = useState<string | null>(null);
	const [qrSvg, setQrSvg] = useState<string | null>(null);
	const [code, setCode] = useState<string[]>(["", "", "", "", "", ""]);
	const [error, setError] = useState<string | null>(null);
	const refs = useRef<(HTMLInputElement | null)[]>([]);

	useEffect(() => {
		let alive = true;
		setupMutation
			.mutateAsync()
			.then((res) => {
				if (!alive) return;
				setSetupId(res.setupId);
				setSecret(res.secret);
				setQrSvg(res.qrSvg);
			})
			.catch((err) => {
				if (!alive) return;
				setError(
					extractErrorMessage(err, "Initialisation du setup impossible"),
				);
			});
		return () => {
			alive = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const verify = async (full: string) => {
		if (!setupId) return;
		setError(null);
		try {
			const res = await verifyMutation.mutateAsync({
				setupId,
				code: full,
				label: "Authenticator",
			});
			// Si premiers codes de récupération générés, on les passe à la page suivante
			if (res.recoveryCodes && res.recoveryCodes.length > 0) {
				navigate("/profil/securite/2fa/recovery-codes", {
					state: { codes: res.recoveryCodes },
				});
			} else {
				navigate("/profil/securite");
			}
		} catch (err) {
			setError(extractErrorMessage(err, "Code incorrect"));
			setCode(["", "", "", "", "", ""]);
			refs.current[0]?.focus();
		}
	};

	const setDigit = (i: number, v: string) => {
		const d = v.replace(/\D/g, "").slice(-1);
		const nc = [...code];
		nc[i] = d;
		setCode(nc);
		if (error) setError(null);
		if (d && i < 5) refs.current[i + 1]?.focus();
		if (nc.every((x) => x) && i === 5) void verify(nc.join(""));
	};

	const onKey = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Backspace" && !code[i] && i > 0)
			refs.current[i - 1]?.focus();
	};

	const onPaste = (e: ClipboardEvent<HTMLDivElement>) => {
		const v = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
		if (v.length === 6) {
			e.preventDefault();
			setCode(v.split(""));
			void verify(v);
		}
	};

	const allFilled = code.every((x) => x);

	return (
		<div>
			<div className="crumbs" style={{ marginBottom: 14, fontSize: 12 }}>
				<a
					className="root"
					style={{ cursor: "pointer" }}
					onClick={() => navigate("/profil/securite")}
				>
					Sécurité
				</a>
				<span className="sep">
					<AdminIcon n="chevR" s={10} />
				</span>
				<a
					className="root"
					style={{ cursor: "pointer" }}
					onClick={() => navigate("/profil/securite/2fa/methode")}
				>
					Ajouter une méthode
				</a>
				<span className="sep">
					<AdminIcon n="chevR" s={10} />
				</span>
				<span className="here">TOTP</span>
			</div>

			<Card style={{ maxWidth: 720 }}>
				<CardHead
					title="Configurer une application authenticator"
					sub="Scannez le QR code avec Google Authenticator, Authy ou équivalent puis saisissez le code à 6 chiffres."
				/>
				<CardBody>
					{setupMutation.isPending && (
						<div className="muted">Génération du QR code…</div>
					)}

					{qrSvg && secret && (
						<>
							<div
								style={{
									display: "flex",
									gap: 24,
									alignItems: "flex-start",
									marginBottom: 20,
								}}
							>
								<div
									style={{
										width: 200,
										height: 200,
										background: "#fff",
										border: "1px solid var(--a-border)",
										borderRadius: 10,
										padding: 8,
									}}
									data-testid="mfa-qr"
									dangerouslySetInnerHTML={{ __html: qrSvg }}
								/>
								<div
									style={{
										flex: 1,
										fontSize: 12.5,
										color: "var(--a-ink-soft)",
									}}
								>
									<strong
										style={{
											color: "var(--a-ink)",
											display: "block",
											marginBottom: 8,
										}}
									>
										Vous ne pouvez pas scanner ?
									</strong>
									Entrez ce secret manuellement dans votre application :
									<div
										style={{
											marginTop: 8,
											padding: "8px 10px",
											background: "var(--a-surface-2)",
											borderRadius: 6,
											fontFamily: "var(--a-font-mono)",
											fontSize: 12,
											letterSpacing: "0.06em",
											wordBreak: "break-all",
										}}
										data-testid="mfa-secret"
									>
										{secret}
									</div>
									<div
										className="muted"
										style={{ fontSize: 11, marginTop: 12 }}
									>
										Algorithme SHA-1 · 6 chiffres · 30 secondes
									</div>
								</div>
							</div>

							<div className="eyebrow" style={{ marginBottom: 8 }}>
								Code de vérification à 6 chiffres
							</div>
							<div
								className="otp-row"
								onPaste={onPaste}
								style={{ margin: "0 0 12px" }}
							>
								{code.map((v, i) => (
									<input
										key={i}
										ref={(el) => {
											refs.current[i] = el;
										}}
										className={
											"otp-input " +
											(v ? "filled" : "") +
											(error ? "error" : "")
										}
										type="text"
										inputMode="numeric"
										maxLength={1}
										value={v}
										onChange={(e) => setDigit(i, e.target.value)}
										onKeyDown={(e) => onKey(i, e)}
										data-testid={`mfa-totp-digit-${i}`}
									/>
								))}
							</div>

							{error && (
								<div className="auth-alert danger" data-testid="mfa-error">
									<span className="icon">
										<AdminIcon n="alert" s={16} />
									</span>
									<div>{error}</div>
								</div>
							)}

							<div
								style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
							>
								<AdminButton
									variant="ghost"
									onClick={() => navigate("/profil/securite")}
								>
									Annuler
								</AdminButton>
								<AdminButton
									variant="primary"
									onClick={() => void verify(code.join(""))}
									disabled={!allFilled || verifyMutation.isPending}
									data-testid="mfa-verify-submit"
								>
									{verifyMutation.isPending
										? "Vérification…"
										: "Activer la méthode"}
									<AdminIcon n="check" s={14} c="#fff" />
								</AdminButton>
							</div>
						</>
					)}
				</CardBody>
			</Card>
		</div>
	);
}
